import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  TextField,
  InputAdornment,
  IconButton,
  Paper,
  List,
  ListItem,
  ListItemText,
  Typography,
  Box,
  Chip,
  Popper,
  ClickAwayListener,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  History as HistoryIcon,
  TrendingUp as TrendingIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';
import {
  setQuery,
  clearQuery,
  performSearch,
  getSuggestions,
  setShowSuggestions,
  loadSearchHistory,
  saveSearchHistory,
  selectSearchQuery,
  selectSearchSuggestions,
  selectSearchHistory,
  selectIsSearching,
} from '../../redux/slices/searchSlice';

const SearchBar = ({ 
  placeholder = "Search documents, products, and more...", 
  onSearch,
  autoFocus = false,
  size = "medium",
  fullWidth = true,
  showSuggestions = true,
  showHistory = true,
  onCollapse,
}) => {
  const dispatch = useDispatch();
  const query = useSelector(selectSearchQuery);
  const suggestions = useSelector(selectSearchSuggestions);
  const searchHistory = useSelector(selectSearchHistory);
  const isSearching = useSelector(selectIsSearching);

  const [focused, setFocused] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const inputRef = useRef(null);
  const searchBarRef = useRef(null);

  // Debounced suggestion fetching
  const debouncedGetSuggestions = useMemo(
    () => debounce((searchQuery) => {
      if (searchQuery && searchQuery.length >= 2) {
        dispatch(getSuggestions(searchQuery));
      }
    }, 150), // Reduced from 300ms to 150ms for faster suggestions
    [dispatch]
  );
  
  // Debounced search execution for auto-search
  const debouncedPerformSearch = useMemo(
    () => debounce((searchQuery) => {
      if (searchQuery && searchQuery.trim().length >= 2 && onSearch) {
        onSearch(searchQuery);
      }
    }, 400), // Reduced from 800ms to 400ms for faster search
    [onSearch]
  );

  // Handle input change
  const handleInputChange = useCallback((event) => {
    const value = event.target.value;
    dispatch(setQuery(value));
    
    if (showSuggestions && value.length >= 2) {
      debouncedGetSuggestions(value);
    }
    
    // Auto-search for longer queries to reduce server load
    if (value.length >= 3) {
      debouncedPerformSearch(value);
    }
  }, [dispatch, showSuggestions, debouncedGetSuggestions, debouncedPerformSearch]);

  // Handle search submission
  const handleSearch = useCallback((searchQuery = query) => {
    if (!searchQuery.trim()) return;
    
    // Cancel any pending debounced searches
    debouncedPerformSearch.cancel();
    
    dispatch(setShowSuggestions(false));
    setAnchorEl(null);
    
    if (onSearch) {
      onSearch(searchQuery);
    } else {
      // Default search with current settings
      dispatch(performSearch({
        query: searchQuery,
        modules: ['core', 'mm', 'crm'], // Default modules
        offset: 0,
      }));
    }
  }, [query, dispatch, onSearch, debouncedPerformSearch]);

  // Handle key press
  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    } else if (event.key === 'Escape') {
      dispatch(setShowSuggestions(false));
      setAnchorEl(null);
    }
  };

  // Handle focus
  const handleFocus = () => {
    setFocused(true);
    setAnchorEl(searchBarRef.current);
  };

  // Handle blur
  const handleBlur = () => {
    setFocused(false);
    // Delay hiding suggestions to allow for clicks
    setTimeout(() => {
      if (!searchBarRef.current?.contains(document.activeElement)) {
        setAnchorEl(null);
        // Collapse back to the icon when the field is left empty
        if (onCollapse && !query.trim()) {
          onCollapse();
        }
      }
    }, 200);
  };

  // Handle suggestion click
  const handleSuggestionClick = useCallback((suggestion) => {
    dispatch(setQuery(suggestion));
    handleSearch(suggestion);
  }, [dispatch, handleSearch]);

  // Handle history item click
  const handleHistoryClick = useCallback((historyItem) => {
    dispatch(setQuery(historyItem.query));
    handleSearch(historyItem.query);
  }, [dispatch, handleSearch]);

  // Clear search
  const handleClear = useCallback(() => {
    debouncedGetSuggestions.cancel();
    debouncedPerformSearch.cancel();
    dispatch(clearQuery());
    inputRef.current?.focus();
  }, [dispatch, debouncedGetSuggestions, debouncedPerformSearch]);

  // Show suggestions dropdown
  const showSuggestionsDropdown = focused && anchorEl && (
    (showSuggestions && suggestions.length > 0) ||
    (showHistory && searchHistory.length > 0 && !query)
  );

  // Cleanup debounced functions on unmount
  useEffect(() => {
    return () => {
      debouncedGetSuggestions.cancel();
      debouncedPerformSearch.cancel();
    };
  }, [debouncedGetSuggestions, debouncedPerformSearch]);

  // Load persisted search history once per app mount
  const historyLoadedRef = useRef(false);
  useEffect(() => {
    if (historyLoadedRef.current) return;
    historyLoadedRef.current = true;
    dispatch(loadSearchHistory());
  }, [dispatch]);

  // Persist history to the server on change (debounced 1s). Only saves if history
  // actually looks like user-entered data (skips the initial empty state).
  const saveHistoryTimer = useRef(null);
  const lastSavedHistoryRef = useRef(null);
  useEffect(() => {
    if (!historyLoadedRef.current) return;
    const serialised = JSON.stringify(searchHistory);
    if (serialised === lastSavedHistoryRef.current) return;
    if (saveHistoryTimer.current) clearTimeout(saveHistoryTimer.current);
    saveHistoryTimer.current = setTimeout(() => {
      lastSavedHistoryRef.current = serialised;
      dispatch(saveSearchHistory(searchHistory));
    }, 1000);
    return () => {
      if (saveHistoryTimer.current) clearTimeout(saveHistoryTimer.current);
    };
  }, [searchHistory, dispatch]);

  return (
    <Box ref={searchBarRef} sx={{ position: 'relative', width: fullWidth ? '100%' : 'auto' }}>
      <TextField
        ref={inputRef}
        fullWidth={fullWidth}
        size={size}
        value={query}
        onChange={handleInputChange}
        onKeyPress={handleKeyPress}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        autoFocus={autoFocus}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              {isSearching ? (
                <CircularProgress size={20} />
              ) : (
                <SearchIcon color="action" />
              )}
            </InputAdornment>
          ),
          endAdornment: query && (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={handleClear}
                edge="end"
                aria-label="clear search"
              >
                <ClearIcon />
              </IconButton>
            </InputAdornment>
          ),
          sx: {
            borderRadius: 2,
            backgroundColor: 'background.paper',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
            '&.Mui-focused': {
              backgroundColor: 'background.paper',
            },
          },
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            '&:hover fieldset': {
              borderColor: 'primary.main',
            },
          },
        }}
      />

      <Popper
        open={showSuggestionsDropdown}
        anchorEl={anchorEl}
        placement="bottom-start"
        sx={{ zIndex: 1300, width: anchorEl?.clientWidth || 'auto' }}
      >
        <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
          <Paper
            elevation={3}
            sx={{
              mt: 0.5,
              maxHeight: 400,
              overflow: 'auto',
              border: 1,
              borderColor: 'divider',
            }}
          >
            {/* Search Suggestions */}
            {showSuggestions && suggestions.length > 0 && (
              <>
                <Box sx={{ px: 2, py: 1 }}>
                  <Typography variant="caption" color="textSecondary">
                    Suggestions
                  </Typography>
                </Box>
                <List dense>
                  {suggestions.map((suggestion, index) => (
                    <ListItem
                      key={index}
                      button
                      onClick={() => handleSuggestionClick(suggestion)}
                      sx={{
                        py: 0.5,
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                    >
                      <TrendingIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                      <ListItemText
                        primary={suggestion}
                        primaryTypographyProps={{
                          variant: 'body2',
                          sx: { fontWeight: query && suggestion.toLowerCase().includes(query.toLowerCase()) ? 600 : 400 }
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            )}

            {/* Divider between suggestions and history */}
            {showSuggestions && suggestions.length > 0 && showHistory && searchHistory.length > 0 && (
              <Divider />
            )}

            {/* Search History */}
            {showHistory && searchHistory.length > 0 && !query && (
              <>
                <Box sx={{ px: 2, py: 1 }}>
                  <Typography variant="caption" color="textSecondary">
                    Recent Searches
                  </Typography>
                </Box>
                <List dense>
                  {searchHistory.slice(0, 5).map((historyItem) => (
                    <ListItem
                      key={historyItem.id}
                      button
                      onClick={() => handleHistoryClick(historyItem)}
                      sx={{
                        py: 0.5,
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                    >
                      <HistoryIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                      <ListItemText
                        primary={historyItem.query}
                        secondary={
                          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                            {historyItem.modules?.map((module) => (
                              <Chip
                                key={module}
                                label={module}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.65rem', height: 16 }}
                              />
                            ))}
                          </Box>
                        }
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            )}

            {/* Empty state */}
            {(!suggestions.length && !searchHistory.length) && (
              <Box sx={{ px: 2, py: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="textSecondary">
                  Start typing to see suggestions
                </Typography>
              </Box>
            )}
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};

export default SearchBar;